import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface RevisionMessage {
  id: string;
  order_id: string;
  sender_type: 'customer' | 'admin';
  sender_email: string;
  message: string;
  created_at: string;
}

interface AdminRevisionChatProps {
  orderId: string;
  adminEmail: string;
  customerName: string;
}

const EMOJI_LIST = [
  '😊', '😍', '👍', '👎', '🙏', '💯', '✅', '❌',
  '🎨', '🖌️', '📐', '✏️', '🔄', '⚠️', '💡', '🤔',
  '😃', '😢', '🎉', '🔥', '⭐', '💪', '👏', '🙌',
];

export default function AdminRevisionChat({ orderId, adminEmail, customerName }: AdminRevisionChatProps) {
  const [messages, setMessages] = useState<RevisionMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessages();

    const subscription = supabase
      .channel(`admin-design-revisions-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'design_revisions',
          filter: `order_id=eq.${orderId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [orderId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('design_revisions')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (data) setMessages(data);
    } catch (error) {
      console.error('Mesajlar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('design_revisions')
        .insert({
          order_id: orderId,
          sender_type: 'admin',
          sender_email: adminEmail,
          message: newMessage.trim(),
        });

      if (error) {
        console.error('Supabase error:', error);
        alert('Mesaj gönderilemedi: ' + error.message);
        throw error;
      }
      
      setNewMessage('');
      setShowEmoji(false);
      setShowReplyInput(false);
    } catch (error) {
      console.error('Mesaj gönderilirken hata:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const insertEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <i className="ri-loader-4-line text-xl text-orange-500 animate-spin"></i>
      </div>
    );
  }

  if (messages.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
          <i className="ri-chat-3-line text-sm text-orange-600"></i>
        </div>
        <h4 className="text-gray-800 font-medium text-sm">Düzenleme Talepleri</h4>
        <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-medium">{messages.length}</span>
      </div>

      <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
        {/* Messages */}
        <div className="max-h-64 overflow-y-auto p-3 space-y-2.5">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%]`}>
                <div
                  className={`px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                    msg.sender_type === 'admin'
                      ? 'bg-green-500 text-white rounded-br-md'
                      : 'bg-white text-gray-800 border border-orange-200 rounded-bl-md shadow-sm'
                  }`}
                >
                  {msg.sender_type === 'customer' && (
                    <div className="flex items-center gap-1.5 mb-1">
                      <i className="ri-user-line text-xs text-orange-500"></i>
                      <span className="text-xs font-medium text-orange-500">{customerName}</span>
                    </div>
                  )}
                  {msg.sender_type === 'admin' && (
                    <div className="flex items-center gap-1.5 mb-1">
                      <i className="ri-team-line text-xs text-green-200"></i>
                      <span className="text-xs font-medium text-green-200">XKartvizitler Ekibi</span>
                    </div>
                  )}
                  {msg.message}
                </div>
                <div className={`flex items-center gap-1 mt-0.5 ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <p className="text-xs text-gray-400">
                    {formatTime(msg.created_at)}
                  </p>
                  <span className="text-xs text-gray-400">✓✓</span>
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Input */}
        {showReplyInput ? (
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex items-end gap-2">
              <div className="relative flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Yanıtınızı yazın..."
                  rows={2}
                  maxLength={500}
                  autoFocus
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 pr-12 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none resize-none"
                />
                <div className="absolute right-3 bottom-2.5" ref={emojiRef}>
                  <button
                    type="button"
                    onClick={() => setShowEmoji(!showEmoji)}
                    className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-green-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-emotion-happy-line text-lg"></i>
                  </button>
                  {showEmoji && (
                    <div className="absolute bottom-10 right-0 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-64 z-10">
                      <div className="grid grid-cols-8 gap-1">
                        {EMOJI_LIST.map((emoji, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => insertEmoji(emoji)}
                            className="w-7 h-7 flex items-center justify-center text-lg hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={handleSend}
                  disabled={sending || !newMessage.trim()}
                  className="w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  {sending ? (
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                  ) : (
                    <i className="ri-send-plane-fill text-lg"></i>
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between mt-1">
              <button
                onClick={() => { setShowReplyInput(false); setNewMessage(''); setShowEmoji(false); }}
                className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
              >
                İptal
              </button>
              <p className="text-gray-400 text-xs">{newMessage.length}/500</p>
            </div>
          </div>
        ) : (
          <div className="p-3 border-t border-gray-200 bg-white">
            <button
              onClick={() => setShowReplyInput(true)}
              className="w-full px-4 py-2.5 bg-green-50 hover:bg-green-100 text-green-700 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap border border-green-200"
            >
              <i className="ri-reply-line text-lg"></i>
              Yanıtla
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
